import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { Mail, Save, Users, ExternalLink, Trash2, RefreshCw } from 'lucide-react';
import api from '../../lib/api';

export const NewsletterAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [config, setConfig] = useState({
    mailchimp_audience_id: '',
    mailchimp_server_prefix: '',
    privacy_policy_url: '',
  });
  const [subscribers, setSubscribers] = useState([]);

  useEffect(() => {
    loadConfig();
    loadSubscribers();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.get('/newsletter/config');
      if (res.data) {
        setConfig(prev => ({ ...prev, ...res.data }));
      }
    } catch (error) {
      console.log('No newsletter config found');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscribers = async () => {
    setLoadingSubscribers(true);
    try {
      const res = await api.get('/newsletter/subscribers');
      setSubscribers(res.data || []);
    } catch (error) {
      console.log('Error loading subscribers');
    } finally {
      setLoadingSubscribers(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/newsletter/config', config);
      toast.success('Configurazione newsletter salvata');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo iscritto?')) return;
    
    try {
      await api.delete(`/newsletter/subscribers/${id}`);
      setSubscribers(prev => prev.filter(s => s.id !== id));
      toast.success('Iscritto eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Mail className="animate-pulse text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="newsletter-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Newsletter</h1>
          <p className="text-muted-foreground">Configura Mailchimp e gestisci gli iscritti</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-newsletter-config">
          <Save size={16} className="mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Configurazione'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mailchimp Config */}
        <Card className="glass border-white/10">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <Mail className="text-yellow-400" size={20} />
              </div>
              <div>
                <CardTitle>Configurazione Mailchimp</CardTitle>
                <CardDescription>Collega il tuo account Mailchimp</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <p className="text-xs text-blue-400">
                <strong>Nota:</strong> L'API Key di Mailchimp viene letta dal file .env del server (MAILCHIMP_API_KEY)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Audience ID (List ID)</Label>
              <Input
                value={config.mailchimp_audience_id}
                onChange={(e) => updateConfig('mailchimp_audience_id', e.target.value)}
                placeholder="abc123def4"
                className="bg-white/5 border-white/10"
                data-testid="input-mailchimp-audience-id"
              />
              <p className="text-xs text-muted-foreground">
                Trova l'Audience ID in Mailchimp: Audience → Settings → Audience name and defaults
              </p>
            </div>

            <div className="space-y-2">
              <Label>Server Prefix</Label>
              <Input
                value={config.mailchimp_server_prefix}
                onChange={(e) => updateConfig('mailchimp_server_prefix', e.target.value)}
                placeholder="us21"
                className="bg-white/5 border-white/10"
                data-testid="input-mailchimp-server"
              />
              <p className="text-xs text-muted-foreground">
                Il prefisso del server si trova nell'URL API (es. us21, us14, ecc.)
              </p>
            </div>

            <div className="space-y-2">
              <Label>Link Privacy Policy</Label>
              <Input
                value={config.privacy_policy_url}
                onChange={(e) => updateConfig('privacy_policy_url', e.target.value)}
                placeholder="https://sito.com/privacy"
                className="bg-white/5 border-white/10"
                data-testid="input-privacy-url"
              />
              <p className="text-xs text-muted-foreground">
                Link alla pagina privacy da mostrare nel form di iscrizione
              </p>
            </div>

            <a 
              href="https://mailchimp.com/help/find-audience-id/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              Come trovare l'Audience ID
              <ExternalLink size={14} />
            </a>
          </CardContent>
        </Card>

        {/* Subscribers List */}
        <Card className="glass border-white/10">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <Users className="text-green-400" size={20} />
                </div>
                <div>
                  <CardTitle>Iscritti Newsletter</CardTitle>
                  <CardDescription>{subscribers.length} iscritti locali</CardDescription>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={loadSubscribers} disabled={loadingSubscribers}>
                <RefreshCw size={16} className={loadingSubscribers ? 'animate-spin' : ''} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {subscribers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessun iscritto ancora</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {subscribers.map((sub) => (
                  <div 
                    key={sub.id} 
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium">{sub.email}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(sub.created_at).toLocaleDateString('it-IT')}
                        {sub.synced_to_mailchimp && (
                          <span className="ml-2 text-green-400">✓ Sincronizzato</span>
                        )}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteSubscriber(sub.id)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewsletterAdminPage;
